import React, { useState, useEffect, useMemo } from "react";
import { Link } from "react-router-dom";
import { Button } from "../../../foundation/altzero_base/base/components/ui/button";
import { Input } from "../../../foundation/altzero_base/base/components/ui/input";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "../../../foundation/altzero_base/base/components/ui/card";
import { ResponsiveTable } from "../components/ResponsiveTable";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "../../../foundation/altzero_base/base/components/ui/dialog";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "../../../foundation/altzero_base/base/components/ui/alert-dialog";
import { Label } from "../../../foundation/altzero_base/base/components/ui/label";
import { useToast } from "../../../foundation/altzero_base/base/hooks/use-toast";
import {
  Store,
  Plus,
  Search,
  Edit,
  Trash2,
  ArrowLeft,
  Mail,
  CheckCircle,
  XCircle,
  Users,
  X,
} from "lucide-react";
import ScopingAILayout from "../components/ScopingAILayout";
import {
  getRetailers,
  createRetailer,
  updateRetailer,
  deleteRetailer,
  uploadRetailerLogo,
  deleteRetailerLogo,
  Retailer,
} from "../services/retailerService";
import {
  getRetailerGroups,
  createRetailerGroup,
  updateRetailerGroup,
  deleteRetailerGroup,
  RetailerGroup,
} from "../services/retailerGroupService";
import { Switch } from "../../../foundation/altzero_base/base/components/ui/switch";
import { Checkbox } from "../../../foundation/altzero_base/base/components/ui/checkbox";
import {
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
} from "../../../foundation/altzero_base/base/components/ui/tabs";
import { RetailerEmailConfig, RetailerEmailConfigData } from "../components/RetailerEmailConfig";
import { PaginationBar } from "../components/email/PaginationBar";
import { usePermissions } from "../../../foundation/altzero_base/base/contextapi/PermissionsContext";
import { canCreateRetailers, canEditRetailers, canDeleteRetailers } from "../lib/resourcePermissions";

export default function RetailersManagement() {
  // Retailers and retailer groups had no gating at all — any role could add, change or
  // delete either. See lib/resourcePermissions.ts.
  const { can } = usePermissions();
  const mayCreateRetailers = canCreateRetailers(can);
  const mayEditRetailers = canEditRetailers(can);
  const mayDeleteRetailers = canDeleteRetailers(can);
  const { toast } = useToast();
  const [activeTab, setActiveTab] = useState("retailers");
  
  // Retailers state
  const [retailers, setRetailers] = useState<Retailer[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState("");
  const [statusFilter, setStatusFilter] = useState<"all" | "active" | "inactive">("all");
  const [retailerPage, setRetailerPage] = useState(1);
  const [retailerPageSize, setRetailerPageSize] = useState(10);
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [isDeleteDialogOpen, setIsDeleteDialogOpen] = useState(false);
  const [editingRetailer, setEditingRetailer] = useState<Retailer | null>(null);
  const [deletingRetailerId, setDeletingRetailerId] = useState<string | null>(null);
  const [formData, setFormData] = useState({
    name: "",
    contact_email: "",
    logo: "",
    active: true,
    email_domains: [] as string[],
    contact_emails: [] as string[],
    sync_enabled: true,
  });
  const [logoFile, setLogoFile] = useState<File | null>(null);
  const [logoPreview, setLogoPreview] = useState<string>("");

  // Groups state
  const [groups, setGroups] = useState<RetailerGroup[]>([]);
  const [groupsLoading, setGroupsLoading] = useState(true);
  const [groupSearchQuery, setGroupSearchQuery] = useState("");
  const [groupPage, setGroupPage] = useState(1);
  const [groupPageSize, setGroupPageSize] = useState(10);
  const [isGroupDialogOpen, setIsGroupDialogOpen] = useState(false);
  const [isGroupDeleteDialogOpen, setIsGroupDeleteDialogOpen] = useState(false);
  const [editingGroup, setEditingGroup] = useState<RetailerGroup | null>(null);
  const [deletingGroupId, setDeletingGroupId] = useState<string | null>(null);
  const [groupFormData, setGroupFormData] = useState({
    name: "",
    retailer_ids: [] as string[],
  });
  const [retailerSearchQuery, setRetailerSearchQuery] = useState("");

  useEffect(() => {
    loadRetailers();
    loadGroups();
  }, []);

  // Filtering and pagination via useMemo
  const filteredRetailers = useMemo(() => {
    let list = retailers;
    if (statusFilter !== "all") {
      list = list.filter(r => statusFilter === "active" ? r.active : !r.active);
    }
    if (searchQuery.trim()) {
      const q = searchQuery.toLowerCase();
      list = list.filter(r =>
        r.name.toLowerCase().includes(q) ||
        r.contact_email?.toLowerCase().includes(q)
      );
    }
    return list;
  }, [retailers, searchQuery, statusFilter]);

  const paginatedRetailers = useMemo(() =>
    filteredRetailers.slice((retailerPage - 1) * retailerPageSize, retailerPage * retailerPageSize),
    [filteredRetailers, retailerPage, retailerPageSize]
  );

  const filteredGroups = useMemo(() => {
    if (!groupSearchQuery.trim()) return groups;
    const q = groupSearchQuery.toLowerCase();
    return groups.filter(g =>
      g.name.toLowerCase().includes(q) ||
      g.retailers?.some(r => r.name.toLowerCase().includes(q))
    );
  }, [groups, groupSearchQuery]);

  const paginatedGroups = useMemo(() =>
    filteredGroups.slice((groupPage - 1) * groupPageSize, groupPage * groupPageSize),
    [filteredGroups, groupPage, groupPageSize]
  );

  const loadRetailers = async () => {
    setLoading(true);
    const result = await getRetailers();
    if (result.success && result.data) {
      setRetailers(result.data);
    } else {
      toast({
        title: "Error",
        description: result.error || "Failed to load retailers",
        variant: "destructive",
      });
    }
    setLoading(false);
  };



  const handleOpenDialog = (retailer?: Retailer) => {
    if (retailer) {
      setEditingRetailer(retailer);
      setFormData({
        name: retailer.name,
        contact_email: retailer.contact_email || "",
        logo: retailer.logo || "",
        active: retailer.active,
        email_domains: retailer.email_domains || [],
        contact_emails: retailer.contact_emails || [],
        sync_enabled: retailer.sync_enabled ?? true,
      });
      setLogoPreview(retailer.logo || "");
    } else {
      setEditingRetailer(null);
      setFormData({
        name: "",
        contact_email: "",
        logo: "",
        active: true,
        email_domains: [],
        contact_emails: [],
        sync_enabled: true,
      });
      setLogoPreview("");
    }
    setLogoFile(null);
    setIsDialogOpen(true);
  };

  const handleCloseDialog = () => {
    setIsDialogOpen(false);
    setEditingRetailer(null);
    setFormData({
      name: "",
      contact_email: "",
      logo: "",
      active: true,
      email_domains: [],
      contact_emails: [],
      sync_enabled: true,
    });
    setLogoFile(null);
    setLogoPreview("");
  };

  const handleLogoChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      // Validate file type
      if (!file.type.startsWith("image/")) {
        toast({
          title: "Invalid File",
          description: "Please upload an image file",
          variant: "destructive",
        });
        return;
      }

      // Validate file size (max 2MB)
      if (file.size > 2 * 1024 * 1024) {
        toast({
          title: "File Too Large",
          description: "Logo must be less than 2MB",
          variant: "destructive",
        });
        return;
      }

      setLogoFile(file);

      // Create preview
      const reader = new FileReader();
      reader.onloadend = () => {
        setLogoPreview(reader.result as string);
      };
      reader.readAsDataURL(file);
    }
  };

  const handleSubmit = async () => {
    if (!formData.name.trim()) {
      toast({
        title: "Validation Error",
        description: "Retailer name is required",
        variant: "destructive",
      });
      return;
    }

    // Cluster H: 5 retailers (ORIGIN, EA, ME, SHELL, AGL) were reported as "offer emails not
    // auto-detected" - in every case the retailer was onboarded with email sync enabled but no
    // domain configured, so incoming offer emails silently never matched. Block this combination
    // at creation time instead of letting it surface as a bug report after the fact. Retailers
    // that don't need automated email parsing can still be created with sync disabled.
    if (formData.sync_enabled && formData.email_domains.length === 0) {
      toast({
        title: "Validation Error",
        description: "Add at least one email domain before enabling email sync — otherwise incoming offer emails from this retailer will never be auto-detected. Turn off email sync if this retailer doesn't need it yet.",
        variant: "destructive",
      });
      return;
    }

    try {
      let logoUrl = formData.logo;

      // Upload new logo if file is selected
      if (logoFile) {
        const uploadResult = await uploadRetailerLogo(logoFile);
        if (uploadResult.success && uploadResult.url) {
          logoUrl = uploadResult.url;

          // Delete old logo if updating and old logo exists
          if (editingRetailer?.logo && editingRetailer.logo !== logoUrl) {
            await deleteRetailerLogo(editingRetailer.logo);
          }
        } else {
          toast({
            title: "Upload Error",
            description: uploadResult.error || "Failed to upload logo",
            variant: "destructive",
          });
          return;
        }
      }

      const dataToSubmit = {
        ...formData,
        logo: logoUrl,
      };

      const result = editingRetailer
        ? await updateRetailer(editingRetailer.id, dataToSubmit)
        : await createRetailer(dataToSubmit);

      if (result.success) {
        toast({
          title: "Success",
          description: `Retailer ${editingRetailer ? "updated" : "created"} successfully`,
        });
        handleCloseDialog();
        loadRetailers();
      } else {
        toast({
          title: "Error",
          description: result.error || `Failed to ${editingRetailer ? "update" : "create"} retailer`,
          variant: "destructive",
        });
      }
    } catch (error: any) {
      toast({
        title: "Error",
        description: error.message || "An unexpected error occurred",
        variant: "destructive",
      });
    }
  };

  const handleDeleteClick = (retailerId: string) => {
    setDeletingRetailerId(retailerId);
    setIsDeleteDialogOpen(true);
  };

  const handleDeleteConfirm = async () => {
    if (!deletingRetailerId) return;

    const result = await deleteRetailer(deletingRetailerId);

    if (result.success) {
      toast({
        title: "Success",
        description: "Retailer deleted successfully",
      });
      loadRetailers();
      loadGroups(); // Reload groups in case this retailer was in a group
    } else {
      toast({
        title: "Error",
        description: result.error || "Failed to delete retailer",
        variant: "destructive",
      });
    }

    setIsDeleteDialogOpen(false);
    setDeletingRetailerId(null);
  };

  // Groups functions
  const loadGroups = async () => {
    setGroupsLoading(true);
    const result = await getRetailerGroups();
    if (result.success && result.data) {
      setGroups(result.data);
    } else {
      toast({
        title: "Error",
        description: result.error || "Failed to load groups",
        variant: "destructive",
      });
    }
    setGroupsLoading(false);
  };



  const handleOpenGroupDialog = (group?: RetailerGroup) => {
    if (group) {
      setEditingGroup(group);
      setGroupFormData({
        name: group.name,
        retailer_ids: group.retailer_ids || group.retailers?.map((r) => r.id) || [],
      });
    } else {
      setEditingGroup(null);
      setGroupFormData({
        name: "",
        retailer_ids: [],
      });
    }
    setIsGroupDialogOpen(true);
  };

  const handleCloseGroupDialog = () => {
    setIsGroupDialogOpen(false);
    setEditingGroup(null);
    setGroupFormData({
      name: "",
      retailer_ids: [],
    });
    setRetailerSearchQuery("");
  };

  const handleGroupSubmit = async () => {
    if (!groupFormData.name.trim()) {
      toast({
        title: "Validation Error",
        description: "Group name is required",
        variant: "destructive",
      });
      return;
    }

    if (groupFormData.retailer_ids.length === 0) {
      toast({
        title: "Validation Error",
        description: "At least one retailer must be selected",
        variant: "destructive",
      });
      return;
    }

    const result = editingGroup
      ? await updateRetailerGroup(editingGroup.id, groupFormData)
      : await createRetailerGroup(groupFormData);

    if (result.success) {
      toast({
        title: "Success",
        description: `Group ${editingGroup ? "updated" : "created"} successfully`,
      });
      handleCloseGroupDialog();
      loadGroups();
    } else {
      toast({
        title: "Error",
        description: result.error || `Failed to ${editingGroup ? "update" : "create"} group`,
        variant: "destructive",
      });
    }
  };

  const handleGroupDeleteClick = (groupId: string) => {
    setDeletingGroupId(groupId);
    setIsGroupDeleteDialogOpen(true);
  };

  const handleGroupDeleteConfirm = async () => {
    if (!deletingGroupId) return;

    const result = await deleteRetailerGroup(deletingGroupId);

    if (result.success) {
      toast({
        title: "Success",
        description: "Group deleted successfully",
      });
      loadGroups();
    } else {
      toast({
        title: "Error",
        description: result.error || "Failed to delete group",
        variant: "destructive",
      });
    }

    setIsGroupDeleteDialogOpen(false);
    setDeletingGroupId(null);
  };

  const toggleRetailerInGroup = (retailerId: string) => {
    setGroupFormData((prev) => {
      const isSelected = prev.retailer_ids.includes(retailerId);
      return {
        ...prev,
        retailer_ids: isSelected
          ? prev.retailer_ids.filter((id) => id !== retailerId)
          : [...prev.retailer_ids, retailerId],
      };
    });
  };

  // Filter retailers for group dialog
  const filteredRetailersForGroup = useMemo(() => {
    if (!retailerSearchQuery.trim()) {
      return retailers;
    }
    const query = retailerSearchQuery.toLowerCase();
    return retailers.filter(
      (retailer) =>
        retailer.name.toLowerCase().includes(query) ||
        retailer.contact_email?.toLowerCase().includes(query)
    );
  }, [retailers, retailerSearchQuery]);

  return (
    <ScopingAILayout>
      <div className="w-full py-6 px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <div className="mb-6 sm:mb-8">
          <Link
            to="/tender/resource-library"
            className="inline-flex items-center text-sm text-muted-foreground hover:text-primary mb-4 transition-colors"
          >
            <ArrowLeft className="mr-2 h-4 w-4" />
            Back to Resource Library
          </Link>
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
            <div>
              <h1 className="text-3xl sm:text-4xl font-bold text-gray-900 flex items-center gap-3">
                <Store className="w-8 h-8 sm:w-10 sm:h-10 text-primary" />
                Retailers Management
              </h1>
              <p className="text-base sm:text-lg text-muted-foreground mt-2">
                Manage energy retailers and providers
              </p>
            </div>
            {activeTab === "retailers" && mayCreateRetailers && (
              <Button
                onClick={() => handleOpenDialog()}
                size="lg"
                className="w-full sm:w-auto"
              >
                <Plus className="mr-2 h-5 w-5" />
                Add Retailer
              </Button>
            )}
          </div>
        </div>

        {/* Tabs */}
        <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
          <TabsList className="mb-6">
            <TabsTrigger value="retailers" className="flex items-center gap-2">
              <Store className="h-4 w-4" />
              Retailers ({filteredRetailers.length})
            </TabsTrigger>
            <TabsTrigger value="groups" className="flex items-center gap-2">
              <Users className="h-4 w-4" />
              Groups ({filteredGroups.length})
            </TabsTrigger>
          </TabsList>

          {/* Retailers Tab */}
          <TabsContent value="retailers" className="space-y-6">
            {/* Search + Filters */}
            <Card>
              <CardContent className="pt-6 space-y-3">
                <div className="relative">
                  <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-muted-foreground h-5 w-5" />
                  <Input
                    placeholder="Search retailers by name or email..."
                    value={searchQuery}
                    onChange={(e) => { setSearchQuery(e.target.value); setRetailerPage(1); }}
                    className="pl-10 h-12 text-base"
                  />
                </div>
                <div className="flex items-center gap-2">
                  <span className="text-sm text-muted-foreground">Status:</span>
                  {(["all", "active", "inactive"] as const).map(s => (
                    <button
                      key={s}
                      onClick={() => { setStatusFilter(s); setRetailerPage(1); }}
                      className={`px-3 py-1 rounded-full text-xs font-medium border transition-colors ${
                        statusFilter === s
                          ? "bg-primary text-white border-primary"
                          : "bg-white text-gray-600 border-gray-200 hover:border-primary/50"
                      }`}
                    >
                      {s.charAt(0).toUpperCase() + s.slice(1)}
                    </button>
                  ))}
                  <span className="ml-auto text-xs text-muted-foreground">{filteredRetailers.length} retailer{filteredRetailers.length !== 1 ? "s" : ""}</span>
                </div>
              </CardContent>
            </Card>

            {/* Retailers Table */}
            <Card>
              <CardHeader>
                <CardTitle>All Retailers ({filteredRetailers.length})</CardTitle>
                <CardDescription>
                  Energy retailers in your system
                </CardDescription>
              </CardHeader>
              <CardContent>
            {loading ? (
              <div className="text-center py-8 text-muted-foreground">
                Loading retailers...
              </div>
            ) : (
              <ResponsiveTable
                data={paginatedRetailers}
                columns={[
                  {
                    key: "logo",
                    label: "Logo",
                    render: (retailer) => (
                      <div className="flex items-center justify-center">
                        {retailer.logo ? (
                          <img
                            src={retailer.logo}
                            alt={`${retailer.name} logo`}
                            className="h-10 w-10 object-contain rounded"
                            onError={(e) => {
                              // Fallback if image fails to load
                              e.currentTarget.style.display = 'none';
                            }}
                          />
                        ) : (
                          <div className="h-10 w-10 bg-muted rounded flex items-center justify-center">
                            <Store className="h-5 w-5 text-muted-foreground" />
                          </div>
                        )}
                      </div>
                    ),
                  },
                  {
                    key: "name",
                    label: "Name",
                    render: (retailer) => (
                      <span className="font-medium">{retailer.name}</span>
                    ),
                  },
                  {
                    key: "contact_email",
                    label: "Contact Email",
                    render: (retailer) =>
                      retailer.contact_email ? (
                        <div className="flex items-center gap-2">
                          <Mail className="h-4 w-4 text-muted-foreground" />
                          {retailer.contact_email}
                        </div>
                      ) : (
                        <span className="text-muted-foreground">—</span>
                      ),
                  },
                  {
                    key: "active",
                    label: "Status",
                    render: (retailer) => (
                      <div className="flex items-center gap-2">
                        {retailer.active ? (
                          <>
                            <CheckCircle className="h-4 w-4 text-green-600" />
                            <span className="text-green-600 font-medium">Active</span>
                          </>
                        ) : (
                          <>
                            <XCircle className="h-4 w-4 text-gray-400" />
                            <span className="text-gray-400">Inactive</span>
                          </>
                        )}
                      </div>
                    ),
                  },
                ]}
                actions={(retailer) => (
                  <>
                    {mayEditRetailers && (
                      <Button
                        variant="ghost"
                        size="icon"
                        onClick={() => handleOpenDialog(retailer)}
                      >
                        <Edit className="h-4 w-4" />
                      </Button>
                    )}
                    {mayDeleteRetailers && (
                      <Button
                        variant="ghost"
                        size="icon"
                        onClick={() => handleDeleteClick(retailer.id)}
                      >
                        <Trash2 className="h-4 w-4 text-destructive" />
                      </Button>
                    )}
                  </>
                )}
                mobileCardRender={(retailer) => (
                  <div className="flex gap-4">
                    {/* Logo */}
                    <div className="flex-shrink-0">
                      {retailer.logo ? (
                        <img
                          src={retailer.logo}
                          alt={`${retailer.name} logo`}
                          className="h-16 w-16 object-contain rounded border"
                        />
                      ) : (
                        <div className="h-16 w-16 bg-muted rounded border flex items-center justify-center">
                          <Store className="h-8 w-8 text-muted-foreground" />
                        </div>
                      )}
                    </div>
                    {/* Details */}
                    <div className="flex-1 space-y-2">
                      <div className="font-medium text-lg">{retailer.name}</div>
                      {retailer.contact_email && (
                        <div className="flex items-center gap-2 text-sm text-muted-foreground">
                          <Mail className="h-4 w-4" />
                          {retailer.contact_email}
                        </div>
                      )}
                      <div className="flex items-center gap-2">
                        {retailer.active ? (
                          <>
                            <CheckCircle className="h-4 w-4 text-green-600" />
                            <span className="text-green-600 font-medium text-sm">Active</span>
                          </>
                        ) : (
                          <>
                            <XCircle className="h-4 w-4 text-gray-400" />
                            <span className="text-gray-400 text-sm">Inactive</span>
                          </>
                        )}
                      </div>
                    </div>
                  </div>
                )}
                emptyMessage="No retailers found. Add your first retailer to get started."
              />
            )}
            {filteredRetailers.length > 0 && (
              <PaginationBar
                page={retailerPage}
                total={filteredRetailers.length}
                pageSize={retailerPageSize}
                onPageChange={setRetailerPage}
                onPageSizeChange={(s) => { setRetailerPageSize(s); setRetailerPage(1); }}
              />
            )}
          </CardContent>
        </Card>
          </TabsContent>

          {/* Groups Tab */}
          <TabsContent value="groups" className="space-y-6">
            {/* Search */}
            <Card>
              <CardContent className="pt-6">
                <div className="relative">
                  <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-muted-foreground h-5 w-5" />
                  <Input
                    placeholder="Search groups by name or retailer..."
                    value={groupSearchQuery}
                    onChange={(e) => { setGroupSearchQuery(e.target.value); setGroupPage(1); }}
                    className="pl-10 h-12 text-base"
                  />
                </div>
              </CardContent>
            </Card>

            {/* Groups Table */}
            <Card>
              <CardHeader>
                <div className="flex items-center justify-between">
                  <div>
                    <CardTitle>Retailer Groups ({filteredGroups.length})</CardTitle>
                    <CardDescription>
                      Organize retailers into groups for easier management
                    </CardDescription>
                  </div>
                  <Button
                    onClick={() => handleOpenGroupDialog()}
                    size="lg"
                  >
                    <Plus className="mr-2 h-5 w-5" />
                    Create Group
                  </Button>
                </div>
              </CardHeader>
              <CardContent>
                {groupsLoading ? (
                  <div className="text-center py-8 text-muted-foreground">
                    Loading groups...
                  </div>
                ) : (
                  <ResponsiveTable
                    data={paginatedGroups}
                    columns={[
                      {
                        key: "name",
                        label: "Group Name",
                        render: (group) => (
                          <div className="flex items-center gap-2">
                            <Users className="h-5 w-5 text-primary" />
                            <span className="font-medium">{group.name}</span>
                          </div>
                        ),
                      },
                      {
                        key: "retailers",
                        label: "Retailers",
                        render: (group) => (
                          <div className="flex flex-wrap gap-2">
                            {group.retailers && group.retailers.length > 0 ? (
                              group.retailers.slice(0, 3).map((retailer) => (
                                <span
                                  key={retailer.id}
                                  className="inline-flex items-center gap-1 px-2 py-1 bg-muted rounded-md text-sm"
                                >
                                  {retailer.logo ? (
                                    <img
                                      src={retailer.logo}
                                      alt={retailer.name}
                                      className="h-4 w-4 object-contain"
                                    />
                                  ) : (
                                    <Store className="h-3 w-3 text-muted-foreground" />
                                  )}
                                  {retailer.name}
                                </span>
                              ))
                            ) : (
                              <span className="text-muted-foreground">—</span>
                            )}
                            {group.retailers && group.retailers.length > 3 && (
                              <span className="text-sm text-muted-foreground">
                                +{group.retailers.length - 3} more
                              </span>
                            )}
                          </div>
                        ),
                      },
                      {
                        key: "count",
                        label: "Count",
                        render: (group) => (
                          <span className="font-medium">
                            {group.retailer_count || 0} retailer{(group.retailer_count || 0) !== 1 ? "s" : ""}
                          </span>
                        ),
                      },
                    ]}
                    actions={(group) => (
                      <>
                        <Button
                          variant="ghost"
                          size="icon"
                          onClick={() => handleOpenGroupDialog(group)}
                        >
                          <Edit className="h-4 w-4" />
                        </Button>
                        {mayDeleteRetailers && (
                          <Button
                            variant="ghost"
                            size="icon"
                            onClick={() => handleGroupDeleteClick(group.id)}
                          >
                            <Trash2 className="h-4 w-4 text-destructive" />
                          </Button>
                        )}
                      </>
                    )}
                    mobileCardRender={(group) => (
                      <div className="space-y-3">
                        <div className="flex items-center gap-2">
                          <Users className="h-5 w-5 text-primary" />
                          <div className="font-medium text-lg">{group.name}</div>
                        </div>
                        <div className="text-sm text-muted-foreground">
                          {group.retailer_count || 0} retailer{(group.retailer_count || 0) !== 1 ? "s" : ""}
                        </div>
                        {group.retailers && group.retailers.length > 0 && (
                          <div className="flex flex-wrap gap-2">
                            {group.retailers.map((retailer) => (
                              <span
                                key={retailer.id}
                                className="inline-flex items-center gap-1 px-2 py-1 bg-muted rounded-md text-sm"
                              >
                                {retailer.logo ? (
                                  <img
                                    src={retailer.logo}
                                    alt={retailer.name}
                                    className="h-4 w-4 object-contain"
                                  />
                                ) : (
                                  <Store className="h-3 w-3 text-muted-foreground" />
                                )}
                                {retailer.name}
                              </span>
                            ))}
                          </div>
                        )}
                      </div>
                    )}
                    emptyMessage="No groups found. Create your first group to organize retailers."
                  />
                )}
                {filteredGroups.length > 0 && (
                  <PaginationBar
                    page={groupPage}
                    total={filteredGroups.length}
                    pageSize={groupPageSize}
                    onPageChange={setGroupPage}
                    onPageSizeChange={(s) => { setGroupPageSize(s); setGroupPage(1); }}
                  />
                )}
              </CardContent>
            </Card>
          </TabsContent>
        </Tabs>

        {/* Add/Edit Dialog */}
        <Dialog open={isDialogOpen} onOpenChange={setIsDialogOpen}>
          <DialogContent className="sm:max-w-[1400px] max-w-[95vw] flex flex-col max-h-[90vh]">
            <DialogHeader>
              <DialogTitle>
                {editingRetailer ? "Edit Retailer" : "Add New Retailer"}
              </DialogTitle>
              <DialogDescription>
                {editingRetailer
                  ? "Update retailer information"
                  : "Add a new energy retailer to your system"}
              </DialogDescription>
            </DialogHeader>

            <div className="flex-1 overflow-y-auto -mx-6 px-6">
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 py-4">
                {/* Left Section: Basic Information */}
                <div className="space-y-6">
                  <div>
                    <h3 className="text-lg font-semibold mb-4 pb-2 border-b">
                      Basic Information
                    </h3>
                    <div className="space-y-4">
                      <div className="space-y-2">
                        <Label htmlFor="name">
                          Retailer Name <span className="text-red-500">*</span>
                        </Label>
                        <Input
                          id="name"
                          placeholder="e.g., AGL Energy"
                          value={formData.name}
                          onChange={(e) =>
                            setFormData({ ...formData, name: e.target.value })
                          }
                        />
                      </div>
                      <div className="space-y-2">
                        <Label htmlFor="contact_email">Contact Email</Label>
                        <Input
                          id="contact_email"
                          type="email"
                          placeholder="contact@retailer.com"
                          value={formData.contact_email}
                          onChange={(e) =>
                            setFormData({ ...formData, contact_email: e.target.value })
                          }
                        />
                      </div>
                      <div className="space-y-2">
                        <Label htmlFor="logo">Logo</Label>
                        <div className="space-y-2">
                          <Input
                            id="logo"
                            type="file"
                            accept="image/*"
                            onChange={handleLogoChange}
                            className="cursor-pointer"
                          />
                          <p className="text-xs text-muted-foreground">
                            Upload an image file (max 2MB). Supported formats: JPG, PNG, SVG, WebP
                          </p>
                          {logoPreview && (
                            <div className="mt-2 border rounded-lg p-4 bg-muted/50">
                              <img
                                src={logoPreview}
                                alt="Logo preview"
                                className="h-20 w-auto object-contain"
                              />
                            </div>
                          )}
                        </div>
                      </div>
                      <div className="flex items-center justify-between pt-2">
                        <Label htmlFor="active" className="text-base font-medium">
                          Active Status
                        </Label>
                        <Switch
                          id="active"
                          checked={formData.active}
                          onCheckedChange={(checked) =>
                            setFormData({ ...formData, active: checked })
                          }
                        />
                      </div>
                    </div>
                  </div>
                </div>

                {/* Right Section: Email Configuration */}
                <div className="space-y-6">
                  <div>
                    <h3 className="text-lg font-semibold mb-4 pb-2 border-b">
                      Email Configuration
                    </h3>
                    <RetailerEmailConfig
                      value={{
                        emailDomains: formData.email_domains,
                        contactEmails: formData.contact_emails,
                        syncEnabled: formData.sync_enabled,
                      }}
                      onChange={(data) =>
                        setFormData({
                          ...formData,
                          email_domains: data.emailDomains,
                          contact_emails: data.contactEmails,
                          sync_enabled: data.syncEnabled,
                        })
                      }
                    />
                  </div>
                </div>
              </div>
            </div>

            <DialogFooter className="mt-4">
              <Button variant="outline" onClick={handleCloseDialog}>
                Cancel
              </Button>
              <Button onClick={handleSubmit}>
                {editingRetailer ? "Update" : "Create"}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Delete Confirmation Dialog */}
        <AlertDialog open={isDeleteDialogOpen} onOpenChange={setIsDeleteDialogOpen}>
          <AlertDialogContent>
            <AlertDialogHeader>
              <AlertDialogTitle>Are you sure?</AlertDialogTitle>
              <AlertDialogDescription>
                This action cannot be undone. This will permanently delete the
                retailer from your system.
              </AlertDialogDescription>
            </AlertDialogHeader>
            <AlertDialogFooter>
              <AlertDialogCancel>Cancel</AlertDialogCancel>
              <AlertDialogAction onClick={handleDeleteConfirm}>
                Delete
              </AlertDialogAction>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>

        {/* Group Add/Edit Dialog */}
        <Dialog open={isGroupDialogOpen} onOpenChange={setIsGroupDialogOpen}>
          <DialogContent className="sm:max-w-[800px] max-w-[95vw] flex flex-col max-h-[90vh]">
            <DialogHeader>
              <DialogTitle>
                {editingGroup ? "Edit Group" : "Create New Group"}
              </DialogTitle>
              <DialogDescription>
                {editingGroup
                  ? "Update group information and retailers"
                  : "Create a new group and add retailers to it"}
              </DialogDescription>
            </DialogHeader>

            <div className="flex-1 overflow-y-auto -mx-6 px-6">
              <div className="space-y-6 py-4">
                <div className="space-y-2">
                  <Label htmlFor="group-name">
                    Group Name <span className="text-red-500">*</span>
                  </Label>
                  <Input
                    id="group-name"
                    placeholder="e.g., Major Retailers, Regional Providers"
                    value={groupFormData.name}
                    onChange={(e) =>
                      setGroupFormData({ ...groupFormData, name: e.target.value })
                    }
                  />
                </div>

                <div className="space-y-3">
                  <Label>Select Retailers <span className="text-red-500">*</span></Label>
                  <p className="text-sm text-muted-foreground">
                    Select at least one retailer to add to this group
                  </p>
                  {/* Search Input */}
                  <div className="relative">
                    <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-muted-foreground h-4 w-4" />
                    <Input
                      placeholder="Search retailers by name or email..."
                      value={retailerSearchQuery}
                      onChange={(e) => setRetailerSearchQuery(e.target.value)}
                      className="pl-10"
                    />
                  </div>
                  <div className="border rounded-lg p-4 max-h-[400px] overflow-y-auto space-y-3">
                    {retailers.length === 0 ? (
                      <div className="text-center py-8 text-muted-foreground">
                        No retailers available. Please add retailers first.
                      </div>
                    ) : filteredRetailersForGroup.length === 0 ? (
                      <div className="text-center py-8 text-muted-foreground">
                        No retailers found matching "{retailerSearchQuery}"
                      </div>
                    ) : (
                      filteredRetailersForGroup.map((retailer) => {
                        const isSelected = groupFormData.retailer_ids.includes(retailer.id);
                        return (
                          <div
                            key={retailer.id}
                            className="flex items-center space-x-3 p-3 rounded-lg border hover:bg-muted/50 transition-colors cursor-pointer"
                            onClick={() => toggleRetailerInGroup(retailer.id)}
                          >
                            <Checkbox
                              checked={isSelected}
                              onCheckedChange={() => toggleRetailerInGroup(retailer.id)}
                              onClick={(e) => e.stopPropagation()}
                            />
                            <div className="flex items-center gap-3 flex-1">
                              {retailer.logo ? (
                                <img
                                  src={retailer.logo}
                                  alt={retailer.name}
                                  className="h-8 w-8 object-contain rounded"
                                />
                              ) : (
                                <div className="h-8 w-8 bg-muted rounded flex items-center justify-center">
                                  <Store className="h-4 w-4 text-muted-foreground" />
                                </div>
                              )}
                              <div className="flex-1">
                                <div className="font-medium">{retailer.name}</div>
                                {retailer.contact_email && (
                                  <div className="text-sm text-muted-foreground">
                                    {retailer.contact_email}
                                  </div>
                                )}
                              </div>
                              {retailer.active ? (
                                <CheckCircle className="h-4 w-4 text-green-600" />
                              ) : (
                                <XCircle className="h-4 w-4 text-gray-400" />
                              )}
                            </div>
                          </div>
                        );
                      })
                    )}
                  </div>
                  {groupFormData.retailer_ids.length > 0 && (
                    <div className="text-sm text-muted-foreground">
                      {groupFormData.retailer_ids.length} retailer{groupFormData.retailer_ids.length !== 1 ? "s" : ""} selected
                    </div>
                  )}
                </div>
              </div>
            </div>

            <DialogFooter className="mt-4">
              <Button variant="outline" onClick={handleCloseGroupDialog}>
                Cancel
              </Button>
              <Button onClick={handleGroupSubmit}>
                {editingGroup ? "Update" : "Create"}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Group Delete Confirmation Dialog */}
        <AlertDialog open={isGroupDeleteDialogOpen} onOpenChange={setIsGroupDeleteDialogOpen}>
          <AlertDialogContent>
            <AlertDialogHeader>
              <AlertDialogTitle>Are you sure?</AlertDialogTitle>
              <AlertDialogDescription>
                This action cannot be undone. This will permanently delete the
                group from your system. The retailers in this group will not be deleted.
              </AlertDialogDescription>
            </AlertDialogHeader>
            <AlertDialogFooter>
              <AlertDialogCancel>Cancel</AlertDialogCancel>
              <AlertDialogAction onClick={handleGroupDeleteConfirm}>
                Delete
              </AlertDialogAction>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>
      </div>
    </ScopingAILayout>
  );
}
